import React from 'react';
import { cn } from 'utils';
import { timelineLabelWidth } from './constants';
import { TimelineTrackColor } from './types';
import { useTimeline } from './TimelineProvider';

/**
 * The label for the timeline track, which can be used to display a title or other information.
 */
export function TimelineTrackLabel(
  props: React.PropsWithChildren<{ text?: string }>,
): React.ReactElement {
  const { text, children } = props;
  return (
    <div style={{ width: timelineLabelWidth, minWidth: timelineLabelWidth }}>
      {!!text && <h4 className="odin-font-bold">{text}</h4>}
      {children}
    </div>
  );
}

/**
 * Positions content in a timeline slot based either on a specific time or this item's unit increment.
 * Time is prioritized if provided. If not, the unit increment should be which nth item this one is in
 * the overall set of odin-items based on the timeline scale's unit count.
 * The size of the point can be adjusted with the `size` prop, which is a fraction of the overall timeline width.
 */
export function TimelineTrackPoint(
  props: React.PropsWithChildren<{
    size?: number;
    time?: number;
    unitIncrement?: number;
  }>,
): React.ReactElement {
  const { children, size, time, unitIncrement } = props;
  const { getTimelinePositionFromUnitIncrement, getTimelinePositionForTime } =
    useTimeline();

  return (
    !!(time || unitIncrement !== undefined) && (
      <div
        className="odin-absolute odin-top-[50%] odin-translate-y-[-50%] odin-z-[1]"
        style={{
          left: `${
            (time
              ? getTimelinePositionForTime(time)
              : getTimelinePositionFromUnitIncrement(unitIncrement)) * 100
          }%`,
          ...(size ? { width: `${size * 100}%` } : {}),
        }}
      >
        {children}
      </div>
    )
  );
}

const timelineTrackColorClasses: Record<
  TimelineTrackColor | 'default',
  string
> = {
  brand: 'odin-bg-purple-100 odin-border-purple-600',
  danger: 'odin-bg-red-100 odin-border-red-600',
  info: 'odin-bg-blue-100 odin-border-blue-600',
  neutral: 'odin-bg-slate-100 odin-border-slate-600',
  success: 'odin-bg-emerald-100 odin-border-emerald-600',
  default: 'odin-bg-transparent odin-border-transparent',
};

/**
 * The wrapper for content of the timeline track.
 */
export function TimelineTrackSlot(
  props: React.PropsWithChildren<{ color?: TimelineTrackColor | null }>,
): React.ReactElement {
  const { children, color = 'neutral' } = props;
  return (
    <div
      className={cn(
        [
          'odin-border',
          'odin-border-1',
          'odin-flex-1',
          'odin-min-h-[20px]',
          'odin-rounded-md',
          'odin-relative',
          'odin-overflow-hidden',
        ],
        timelineTrackColorClasses[color || 'default'],
      )}
    >
      {children}
    </div>
  );
}

/**
 * The outer container for the timeline track, which contains all the items in the track.
 * The intended structure is:
 *
 * html```
 * <TimelineTrack>
 *   <TimelineTrackLabel text="Label goes here" />
 *   <TimelineTrackSlot>[children go here]</TimelineTrackSlot>
 * </TimelineTrack>
 * ```
 *
 * Note that children for the slot can be specific types such as TimelineChart, TimelineMarkers,
 * TimelineVideoThumbnails, and TimelineWeatherConditions. Or they can be composed using TimelineTrackPoint or manually.
 */
export function TimelineTrack(props: {
  children: React.ReactNode;
}): React.ReactElement {
  const { children } = props;
  return (
    <div className="odin-flex odin-gap-4 odin-items-center odin-w-full">
      {children}
    </div>
  );
}
